import { pushRoute } from '../../lib/router.js';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { createSelector } from 'reselect';
import InitiativeDetail from '../../components/initiatives/InitiativeDetail/index.jsx';
import InitiativeUploadStatus from '../../components/initiatives/InitiativeDetail/InitiativeUploadStatus.jsx';
import { getOrgTermsContent } from '../../content/utils.js';
import { selectInitiativeParticipation } from '../../modules/initiativeParticipations.js';
import {
  retrieveInitiative,
  retrieveInitiativeAncestry,
  retrieveInitiativeParticipations,
  searchInitiativeParticipations,
  selectAncestryInitiativeParticipations,
  selectInitiative,
  selectInitiativeParticipations,
  selectSearchQuery,
  updateSearchQuery,
} from '../../modules/initiatives.js';
import { selectOrganization } from '../../modules/organizations.js';
import {
  retrieveInitiativeStakeholderParticipations,
  searchInitiativeStakeholderParticipations,
  selectInitiativeStakeholders,
} from '../../modules/stakeholderParticipations.js';
import {
  retrievePortfolios,
  selectPortfolio,
} from '../../modules/portfolio.js';

const mapStateToProps = (
  state,
  { initiativeId, initiativeType, initiativeParticipationId, status, sentiment }
) =>
  createSelector(
    selectInitiative(initiativeId, initiativeType),
    selectInitiativeParticipations(
      initiativeId,
      initiativeType,
      initiativeParticipationId,
      status,
      sentiment
    ),
    selectAncestryInitiativeParticipations,
    selectOrganization,
    selectInitiativeParticipation(initiativeParticipationId),
    selectSearchQuery,
    selectInitiativeStakeholders,
    (
      initiative,
      initiativeParticipations,
      ancestryInitiativeParticipations,
      organization,
      initiativeParticipation,
      searchQuery,
      initiativeStakeholders
    ) => {
      return {
        initiative,
        initiativeParticipations,
        ancestryInitiativeParticipations,
        organization,
        initiativeParticipation,
        searchQuery,
        initiativeStakeholders,
        portfolio: initiative?.project?.portfolioId
          ? selectPortfolio(initiative.project.portfolioId)(state)
          : null,
        apiUrl: state.config.apiUrl,
      };
    }
  )(state);

const mapDispatchToProps = (dispatch, { initiativeId, initiativeType }) =>
  bindActionCreators(
    {
      retrievePortfolios: () => retrievePortfolios(),
      retrieveInitiative: () =>
        retrieveInitiative(initiativeType, initiativeId),
      retrieveInitiativeAncestry: () =>
        retrieveInitiativeAncestry(initiativeType, initiativeId),
      retrieveInitiativeParticipations: (initiativeParticipationId) =>
        retrieveInitiativeParticipations(
          initiativeType,
          initiativeId,
          initiativeParticipationId
        ),
      searchInitiativeParticipations: (query) =>
        searchInitiativeParticipations(initiativeType, initiativeId, query),
      retrieveInitiativeStakeholderParticipations: () =>
        retrieveInitiativeStakeholderParticipations(
          initiativeType,
          initiativeId
        ),

      searchInitiativeStakeholderParticipations: (query) =>
        searchInitiativeStakeholderParticipations(
          initiativeType,
          initiativeId,
          query
        ),
      updateSearchQuery,
    },
    dispatch
  );

class InitiativeDetailContainer extends React.PureComponent {
  handleSearch = (event) => {
    const {
      initiativeParticipationId,
      retrieveInitiativeParticipations,
      searchInitiativeParticipations,
      updateSearchQuery,
    } = this.props;
    const query = event.target.value;

    updateSearchQuery(query);
    if (query.length === 0) {
      // Retrieve regular participations when query is blank
      retrieveInitiativeParticipations(initiativeParticipationId);
    } else {
      searchInitiativeParticipations(query);
    }
  };

  handleSearchStakeholders = (event) => {
    const { searchInitiativeStakeholderParticipations } = this.props;
    searchInitiativeStakeholderParticipations(event.target.value);
  };

  redirectToOrigin = () => {
    const { initiativeType, initiativeId } = this.props;

    pushRoute('initiatives/show', {
      initiativeType,
      initiativeId: localStorage.getItem(initiativeId),
    });

    localStorage.removeItem(initiativeId);
  };

  retrieveInitiativeData = () => {
    const {
      initiativeParticipationId,
      retrievePortfolios,
      retrieveInitiative,
      retrieveInitiativeAncestry,
      retrieveInitiativeParticipations,
      retrieveInitiativeStakeholderParticipations,
    } = this.props;

    retrieveInitiative();
    retrieveInitiativeStakeholderParticipations();
    retrievePortfolios();
    retrieveInitiativeParticipations(initiativeParticipationId).then(() =>
      retrieveInitiativeAncestry()
    );
  };

  componentDidMount() {
    this.retrieveInitiativeData();
  }

  componentDidUpdate(prevProps) {
    const {
      initiative,
      initiativeId,
      initiativeType,
      initiativeParticipationId,
      retrieveInitiativeParticipations,
      retrieveInitiativeAncestry,
    } = this.props;

    // Store draft origin ID
    if (initiative?.draftOriginId) {
      localStorage.setItem(initiativeId, initiative.draftOriginId);
    }

    // The route keeps this container mounted when only the initiative in the URL changes
    // (e.g. opening a copy from the initiative page), so load the new initiative
    if (
      initiativeId !== prevProps.initiativeId ||
      initiativeType !== prevProps.initiativeType
    ) {
      this.retrieveInitiativeData();
      return;
    }

    // Fetch new data when initiativeParticipationId changes
    if (initiativeParticipationId !== prevProps.initiativeParticipationId) {
      retrieveInitiativeParticipations(initiativeParticipationId).then(() =>
        retrieveInitiativeAncestry()
      );
    }

    // Retrieve latest data when upload status changes to 'completed'
    if (
      prevProps.initiative &&
      initiative &&
      !InitiativeUploadStatus.completed(prevProps.initiative) &&
      InitiativeUploadStatus.completed(initiative)
    ) {
      this.retrieveInitiativeData();
    }
  }

  render() {
    const { initiative, initiativeId, organization } = this.props;

    if (!initiative && localStorage.getItem(initiativeId)) {
      return (
        <div className="flex flex-col component-xl component-border-bottom text-center">
          <h4 className="text-center" style={{ maxWidth: '430px' }}>
            This preview isn’t available anymore. Please, visit the{' '}
            {getOrgTermsContent('initiative')} page to check the latest
            Participant list.
          </h4>
          <br />
          <button
            onClick={() => this.redirectToOrigin()}
            className="btn btn-success"
          >
            Open {getOrgTermsContent('initiative')} Page
          </button>
        </div>
      );
    }

    if (!initiative || !initiative.project || !organization) {
      return null;
    }

    return (
      <InitiativeDetail
        {...this.props}
        handleSearch={this.handleSearch}
        handleSearchStakeholders={this.handleSearchStakeholders}
      />
    );
  }
}

InitiativeDetailContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(InitiativeDetailContainer);

InitiativeDetailContainer.propTypes = {
  initiativeId: PropTypes.number.isRequired,
  initiativeType: PropTypes.string.isRequired,
  initiativeParticipationId: PropTypes.number,
  status: PropTypes.string,
  sentiment: PropTypes.string,
};

InitiativeDetailContainer.defaultProps = {
  status: null,
  sentiment: null,
};

export default InitiativeDetailContainer;
